import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Tab, TabsContextProvider, TabsHeaderContainer, TabsHeaderContainerPropsType } from "../../../Tabs";
import { useTablePropsContext } from "../../context";

type PredefinedFiltersProps<TPredefinedFilter extends unknown = unknown> = TabsHeaderContainerPropsType & {
  setPredefinedFilter: Dispatch<SetStateAction<TPredefinedFilter>>
};

export function PredefinedFilters<T extends unknown = unknown>({ setPredefinedFilter, ...props }: PredefinedFiltersProps<T>) {
  const [ recordsCount, setRecordsCount ] = useState<(number | undefined)[]>([]);

  const tableProps = useTablePropsContext();
  const options = tableProps?.predefinedFilters;
  const originalData = tableProps?.data;
  const defaultActiveTabId = options?.find(x => x.filter === tableProps?.defaultPredefinedFilter)?.title;

  useEffect(() => {
    const abortController = new AbortController();
    new Promise<void>(async (resolve) => {
      try {
        setRecordsCount([]);
        const numbers = await Promise.all(options?.map(predefinedFilter => {
          if (typeof predefinedFilter.recordsCount === "number") {
            return predefinedFilter.recordsCount;
          }

          if (predefinedFilter.filter) {
            if (typeof predefinedFilter.recordsCount === "function") {
              return predefinedFilter.recordsCount({
                filter: predefinedFilter.filter,
                signal: abortController.signal,
                data: originalData,
              });
            }
          }

          return undefined;
        }) || []);

        if (!abortController.signal.aborted) {
          setRecordsCount(numbers);
        }
      } finally {
        resolve();
      }
    });
    return () => {
      abortController.abort(new Error("Operation aborted by table predefined filters", { cause: "USER_ABORT" }));
    };
  }, [ options, originalData ]);

  if (!options?.length) {
    return null;
  }

  return (
    <TabsContextProvider
      strategy="collapse"
      defaultActiveTabId={defaultActiveTabId}
      onActiveTabIdChanged={tabId => {
        const filter = options.find(x => x.title === tabId)?.filter;
        setPredefinedFilter?.(p => filter as T);
      }}
    >
      <TabsHeaderContainer
        tabContainerType={tableProps?.controlBarView === "regular" ? "underline" : "white"}
        tabContainerSize={tableProps?.controlBarView === "regular" ? "md" : "sm"}
        {...props as any}
        containerDirection="horizontal"
      />
      {
        options.map((value, index) => (
          <Tab
            key={value.title}
            header={value.title}
            id={value.title}
            order={index}
            badgeProps={
              recordsCount[index] !== undefined
                ? {
                  children: recordsCount[index],
                  badgeType: "pillColor",
                  badgeColor: "gray",
                }
                : undefined
            }
          />
        ))
      }
    </TabsContextProvider>
  );
}